import React, { useContext } from 'react';
import { Route, Routes, Navigate, useParams } from 'react-router';
import Operations from '../Operations';
import { IdRoute, NumberRoute, QueryRoute } from '../../types';
import Dashboard from '../Dashboard';
import ResultsHome from '../Results';
import NotificationsPage from '../Notifications';
import RolesAndPermissions from '../Admin';
import GeneralSettings from '../Admin/GeneralSettings';
import MiciCases from '../MiciCases';
import MiciCaseProvider from '../MiciCases/MiciCase/MiciCaseProvider';
import MiciCase from '../MiciCases/MiciCase';
import Missions from '../Missions';
import Mission from '../Missions/Mission';
import MissionProvider from '../Missions/Mission/MissionProvider';
import ExternalApiLogs from '../Admin/ExternalApiLogs';
import MainContext from './MainProvider/context';
import Role from '../../types/role';
import OperationProviderNew from '../Operations/Operation/OperationProviderNew';
import OperationRoutes from '../Operations/Operation/OperationRoutes';
import ReportESInsight from '../PowerBIReports/report_ES_Insight';
import ReportESGrievanceInsight from '../PowerBIReports/report_ES_Grievance_Insight';
import GrievancesGrid from '../Grievances';
import GrievanceFormContainer from '../Grievances/Grievance/FormGrievance/GrievanceFormContainer';
import GrievanceProvider from '../Grievances/Grievance/FormGrievance/GrievanceProvider';
import UIKitTest from '../UIKITTestComponents';
import UIKitMenuDemo from '../UIKITTestComponents/MenuDemo';
import TableExample from '../UIKITTestComponents/testTable';
import SimpleTableWithFooterPagination from '../UIKITTestComponents/tableExpansible';
import ExampleForm from '../UIKITTestComponents/FormTest';
import GrievanceDetailsContextProvider from '../Grievances/Grievance/context/DetailsContextProvider';
import GrievanceDetails from '../Grievances/Grievance';
import { GrievancesModuleContextProvider } from '../Grievances/GrievancesModuleContextProvider';

const isAdmin = (): boolean => {
  const { user } = useContext(MainContext);
  return user.roleCode === Role.Admin;
};

const IfNotAdminRedirectToDashboard: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;
  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const MiciCaseWraper = () => {
  const { number } = useParams() as unknown as NumberRoute;
  return (
    <MiciCaseProvider number={number}>
      <MiciCase />
    </MiciCaseProvider>
  );
};

const MissionWrapper = () => {
  const { id } = useParams() as unknown as IdRoute;
  return (
    <MissionProvider id={id}>
      <Mission />
    </MissionProvider>
  );
};

const OperationProviderWraper = () => {
  const { number } = useParams() as unknown as NumberRoute;
  return (
    <OperationProviderNew number={number}>
      <OperationRoutes />
    </OperationProviderNew>
  );
};

const ResultsWraper = () => {
  const { query } = useParams() as unknown as QueryRoute;
  return <ResultsHome query={query} />;
};

const GrievanceFormProviderWraper = () => (
  <GrievancesModuleContextProvider formAccess>
    <GrievanceProvider>
      <GrievanceFormContainer />
    </GrievanceProvider>
  </GrievancesModuleContextProvider>
);

const GrievaceWraper = () => {
  const { number } = useParams() as unknown as NumberRoute;
  return (
    <GrievancesModuleContextProvider grievanceNumber={number}>
      <GrievanceDetailsContextProvider number={number}>
        <GrievanceDetails />
      </GrievanceDetailsContextProvider>
    </GrievancesModuleContextProvider>
  );
};

const GrievancesModuleWrapper = () => (
  <GrievancesModuleContextProvider>
    <GrievancesGrid />
  </GrievancesModuleContextProvider>
);

const Router: React.FC = () => (
  <Routes>
    <Route path="/notifications/*" element={<NotificationsPage />} />
    <Route path="/results/:query" element={<ResultsWraper />} />
    <Route path="/operations/:number/*" element={<OperationProviderWraper />} />
    <Route path="/operations/*" element={<Operations />} />
    <Route
      path="/admin/*"
      element={
        <IfNotAdminRedirectToDashboard>
          <RolesAndPermissions />
        </IfNotAdminRedirectToDashboard>
      }
    />
    <Route
      path="/general-settings/*"
      element={
        <IfNotAdminRedirectToDashboard>
          <GeneralSettings />
        </IfNotAdminRedirectToDashboard>
      }
    />
    <Route
      path="/api-logs/*"
      element={
        <IfNotAdminRedirectToDashboard>
          <ExternalApiLogs />
        </IfNotAdminRedirectToDashboard>
      }
    />
    <Route path="/mici-cases/:number/*" element={<MiciCaseWraper />} />
    <Route path="/mici-cases/*" element={<MiciCases />} />
    <Route path="/grievance-protocol" element={<GrievancesModuleWrapper />} />
    <Route path="/grievance-protocol/create" element={<GrievanceFormProviderWraper />} />
    <Route path="/grievance-protocol/edit/:number" element={<GrievanceFormProviderWraper />} />
    <Route path="/grievance-protocol/details/:number" element={<GrievaceWraper />} />
    <Route path="/missions/:id/*" element={<MissionWrapper />} />
    <Route path="/missions/*" element={<Missions />} />
    <Route path="/report-es-insight" element={<ReportESInsight />} />
    <Route path="/report-es-grievance-insight" element={<ReportESGrievanceInsight />} />
    <Route path="/" element={<Dashboard />} />
    {process.env.NODE_ENV === 'development' && (
      <>
        <Route path="/test" element={<UIKitTest />} />
        <Route path="/testMenu" element={<UIKitMenuDemo />} />
        <Route path="/tableTest" element={<TableExample />} />
        <Route path="/tableTest1" element={<SimpleTableWithFooterPagination />} />
        <Route path="/formTest" element={<ExampleForm />} />
      </>
    )}
  </Routes>
);

export default Router;
